import * as React from "react";

import { ApiClient } from "@/core/api/client";
import { HealthApi } from "@/core/api/endpoints";
import { awsConfig } from "@/aws-config";
import { fetchTransport } from "@/platform/transport-fetch";
import { amplifyAuthService } from "@/platform/auth-amplify";

interface ApiContextValue {
  client: ApiClient;
  api: HealthApi;
}

const ApiContext = React.createContext<ApiContextValue | null>(null);

export const ApiProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const value = React.useMemo<ApiContextValue>(() => {
    const client = new ApiClient({
      baseUrl: awsConfig.apiBase,
      transport: fetchTransport,
      // REST API Gateway uses a Cognito User Pools authorizer, which validates
      // the ID token. (The WebSocket authorizer uses GetUser → access token.)
      getToken: () => amplifyAuthService.getIdToken(),
      // Cognito's WebAuthn user APIs (passkeys Lambda) require the access
      // token; sent as X-Cognito-Access-Token alongside the ID-token bearer.
      getAccessToken: () => amplifyAuthService.getAccessToken(),
    });
    return { client, api: new HealthApi(client) };
  }, []);
  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
};

export const useApi = (): HealthApi => {
  const ctx = React.useContext(ApiContext);
  if (!ctx) {
    throw new Error("useApi must be used inside <ApiProvider>");
  }
  return ctx.api;
};
